import { Button, UnstyledButton } from '@mantine/core';
import { Spotlight, createSpotlight } from '@mantine/spotlight';
import { Check, CircleAlert, Search } from 'lucide-react';
import { FC, ReactElement, useMemo, useState } from 'react';

import CalendarList from '@/components/CalendarView/CalendarList.tsx';
import { CustomSpotlight } from '@/components/custom-spotlight.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { useAppointmentsForDateRange } from '@/hooks/use-appointments-for-date-range.ts';
import useConfig from '@/hooks/useConfig.ts';
import useFilters from '@/hooks/useFilters.ts';

import { useCalendarStore } from '@/stores/calendarStore.ts';
import { useFilterStore } from '@/stores/filterStore.ts';

import { getBookedCalendarIds } from '@/utils/calendarUtils.ts';

const [calendar_spotlight_store, calendar_spotlight_utils] = createSpotlight();

const CalendarFilter: FC = () => {
    const { calendar_options } = useFilters();
    const { location_key } = useConfig();
    const [search, setSearch] = useState<string>('');

    // Get selected calendars from the filter store
    const selected_calendars = useFilterStore((state) => state.selected_calendars);
    const current_calendar_selection = selected_calendars[location_key] || [];

    const is_only_booked_visible = useCalendarStore((state) => state.is_only_booked_visible);
    const is_week_view_enabled = useCalendarStore((state) => state.is_week_view_enabled);
    const start_date = useFilterStore((state) => state.start_date);
    const end_date = useFilterStore((state) => state.end_date);
    const { appointments, is_loading, is_complete } = useAppointmentsForDateRange(start_date, end_date);

    const bookedCalendarIds = getBookedCalendarIds(appointments);
    const isBookedFilterActive = is_only_booked_visible && !is_week_view_enabled && !is_loading && is_complete;
    const isBookedDataIncomplete = is_only_booked_visible && !is_week_view_enabled && !is_loading && !is_complete;

    // Show every booked calendar at this location, not only the booked part of the current selection.
    const visibleCalendarSelection = isBookedFilterActive
        ? calendar_options.map((option) => option.value).filter((id) => bookedCalendarIds.has(id))
        : current_calendar_selection;

    const handleToggleOnlyBooked = (): void => {
        useCalendarStore.setState({ is_only_booked_visible: !is_only_booked_visible });
    };

    const handleSelectAll = (): void => {
        const all_calendar_ids = calendar_options.map((option) => option.value);

        useCalendarStore.setState({ is_only_booked_visible: false });

        useFilterStore.setState({
            selected_calendars: {
                ...selected_calendars,
                [location_key]: all_calendar_ids,
            },
        });
    };

    const handleUnselectAll = (): void => {
        useCalendarStore.setState({ is_only_booked_visible: false });

        useFilterStore.setState({
            selected_calendars: {
                ...selected_calendars,
                [location_key]: [],
            },
        });
    };

    const handleToggleCalendar = (calendar_id: string): void => {
        // Mutate what the user actually sees. While the Anwesend filter is on, a booked calendar can be
        // displayed and ticked without being in the stored selection, and basing the toggle on the stored
        // selection would re-add it on the first click instead of removing it.
        const is_calendar_selected = visibleCalendarSelection.includes(calendar_id);

        const updated_calendar_selection = is_calendar_selected
            ? visibleCalendarSelection.filter((id) => id !== calendar_id)
            : [...visibleCalendarSelection, calendar_id];

        // A manual pick must win over the Anwesend filter, otherwise the click has no visible effect.
        if (is_only_booked_visible) {
            useCalendarStore.setState({ is_only_booked_visible: false });
        }

        useFilterStore.setState({
            selected_calendars: {
                ...selected_calendars,
                [location_key]: updated_calendar_selection,
            },
        });
    };

    const all_selected = useMemo(() => {
        const all_selected =
            calendar_options.length > 0 &&
            calendar_options.every((option) => current_calendar_selection.includes(option.value));

        return all_selected;
    }, [calendar_options, current_calendar_selection]);

    const getFilteredResults = (): ReactElement[] => {
        const filtered_options = search
            ? calendar_options.filter((option) => option.label.toLowerCase().includes(search.toLowerCase().trim()))
            : calendar_options;

        return filtered_options.map((option) => (
            <Spotlight.Action
                key={option.value}
                onClick={() => handleToggleCalendar(option.value)}
                label={option.label}
                leftSection={
                    <div className="size-4">
                        {visibleCalendarSelection.includes(option.value) && <Check className="size-4 text-green-500" />}
                    </div>
                }
            />
        ));
    };

    const filtered_results = getFilteredResults();

    return (
        <div className="flex h-full flex-col gap-4">
            <div className="flex w-full items-center justify-between">
                {!is_week_view_enabled && (
                    <Button
                        onClick={handleToggleOnlyBooked}
                        size="xs"
                        variant={is_only_booked_visible ? 'filled' : 'default'}
                        aria-pressed={is_only_booked_visible}
                    >
                        Anwesend
                    </Button>
                )}
                <UnstyledButton
                    variant="transparent"
                    onClick={() => (all_selected ? handleUnselectAll() : handleSelectAll())}
                    size="xs"
                    className="ml-auto text-sm text-blue-500"
                >
                    {all_selected ? 'Alle abwählen' : 'Alle auswählen'}
                </UnstyledButton>
            </div>
            <Button
                onClick={calendar_spotlight_utils.open}
                leftSection={<Search className="size-4" />}
                className="w-full"
            >
                {visibleCalendarSelection.length > 0
                    ? `${visibleCalendarSelection.length} Kalender ausgewählt`
                    : 'Kalender auswählen'}
            </Button>
            {isBookedDataIncomplete && (
                <div className="flex items-start gap-2 text-xs text-slate-500">
                    <CircleAlert className="mt-0.5 size-3.5 shrink-0" />
                    <Paragraph>Kalenderdaten unvollständig - Anwesend-Filter ist inaktiv</Paragraph>
                </div>
            )}
            <CalendarList calendarIds={visibleCalendarSelection} />
            <CustomSpotlight
                id="calendar-filter-spotlight"
                closeOnActionTrigger={false}
                spotlightStore={calendar_spotlight_store}
                spotLightUtils={calendar_spotlight_utils}
            >
                <Spotlight.Search
                    placeholder="Kalender suchen..."
                    leftSection={<Search className="size-6" />}
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                />
                <div className="max-h-[50vh] overflow-y-auto">
                    <Spotlight.ActionsList>{filtered_results}</Spotlight.ActionsList>
                </div>
            </CustomSpotlight>
        </div>
    );
};

export default CalendarFilter;
